import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import AppNavbar from '../components/AppNavbar';
import BottomNav from '../components/BottomNav';

const N8N_WIFI_CAMBIO = process.env.REACT_APP_N8N_URL_WIFI_CAMBIO;
const N8N_ACS_OID     = process.env.REACT_APP_N8N_URL_ACS_OID;

type EstadoBanda = 'formulario' | 'enviando' | 'exito' | 'error';

// Clave del campo personalizado donde se guarda el OID de GenieACS (con el que se consulta el servicio)
const CLAVE_OID_GENIEACS = '1770612677';
// Clave del campo personalizado donde se guarda la serie del router
const CLAVE_SERIE_ROUTER = '1770614375';

type ServicioWifi = { index: number; oid: string; serial: string; perfil: string };

// Extrae, de cada servicio, el OID de GenieACS y la serie del router — solo los que tienen ambos
const getServiciosConWifi = (clientData: any): ServicioWifi[] => {
  const servicios = clientData?.servicios;
  if (!Array.isArray(servicios)) return [];
  return servicios
    .map((srv: any, index: number) => {
      try {
        const personalizados = typeof srv.personalizados === 'string' ? JSON.parse(srv.personalizados) : (srv.personalizados || {});
        return {
          index,
          oid: personalizados[CLAVE_OID_GENIEACS] || '',
          serial: personalizados[CLAVE_SERIE_ROUTER] || '',
          perfil: srv.perfil || `Servicio ${index + 1}`,
        };
      } catch { return { index, oid: '', serial: '', perfil: `Servicio ${index + 1}` }; }
    })
    .filter((s: ServicioWifi) => s.oid && s.serial);
};

const CambiarClaveWifi: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Si un admin entra desde la ficha de un cliente (Clientes.tsx), usa los servicios de ESE
  // cliente (recibidos por navigate state); si no, usa la sesión propia del usuario logueado.
  const estadoNav = location.state as { servicios?: any[] } | undefined;
  const clientDataJSON = localStorage.getItem('clientData');
  const sesionPropia = clientDataJSON ? JSON.parse(clientDataJSON) : null;
  const clientData = estadoNav?.servicios ? { servicios: estadoNav.servicios } : sesionPropia;

  const serviciosWifi = clientData ? getServiciosConWifi(clientData) : [];
  const [servicioElegido, setServicioElegido] = useState<ServicioWifi | null>(
    serviciosWifi.length === 1 ? serviciosWifi[0] : null
  );

  const serial = servicioElegido?.serial;
  const idAcs  = servicioElegido?.oid;

  const [cargandoNombres, setCargandoNombres] = useState(false);
  const [nombre24, setNombre24] = useState('');
  const [nombre5, setNombre5]   = useState('');

  const [clave24, setClave24]   = useState('');
  const [verClave24, setVerClave24] = useState(false);
  const [estado24, setEstado24] = useState<EstadoBanda>('formulario');
  const [error24, setError24]   = useState('');

  const [clave5, setClave5]     = useState('');
  const [verClave5, setVerClave5] = useState(false);
  const [estado5, setEstado5]   = useState<EstadoBanda>('formulario');
  const [error5, setError5]     = useState('');

  const [copiado24, setCopiado24] = useState(false);
  const [copiado5, setCopiado5]   = useState(false);

  useEffect(() => {
    if (idAcs && N8N_ACS_OID) {
      setCargandoNombres(true);
      axios.post(N8N_ACS_OID, { id_acs: idAcs })
        .then(res => {
          const redes = res.data?.networks || [];
          setNombre24(redes[0]?.ssid || '');
          setNombre5(redes[1]?.ssid || '');
        })
        .catch(() => {})
        .finally(() => setCargandoNombres(false));
    }
  }, [idAcs]);

  // Al elegir (o cambiar de) servicio, reseteamos los formularios de ambas bandas
  const elegirServicio = (s: ServicioWifi) => {
    setServicioElegido(s);
    setNombre24(''); setNombre5('');
    setClave24(''); setClave5('');
    setEstado24('formulario'); setEstado5('formulario');
    setError24(''); setError5('');
  };

  const claveValida = (c: string) => c.length >= 8;

  const guardarBanda = (banda: '24' | '5') => {
    const clave = banda === '24' ? clave24 : clave5;
    if (!serial || !N8N_WIFI_CAMBIO || !claveValida(clave)) return;

    const setEstado = banda === '24' ? setEstado24 : setEstado5;
    const setError  = banda === '24' ? setError24  : setError5;
    setEstado('enviando');
    setError('');

    const body: Record<string, string> = { serial };
    if (banda === '24') body.clave2g = clave; else body.clave5g = clave;

    axios.post(N8N_WIFI_CAMBIO, body)
      .then(res => {
        if (res.data?.ok === false || res.data?.error) {
          throw new Error(res.data?.error || 'No se pudo aplicar el cambio.');
        }
        setEstado('exito');
      })
      .catch(() => {
        setError('No se pudo aplicar el cambio. Verifica que el router esté conectado e intenta de nuevo.');
        setEstado('error');
      });
  };

  const copiar = (texto: string, banda: '24' | '5') => {
    navigator.clipboard?.writeText(texto).then(() => {
      if (banda === '24') { setCopiado24(true); setTimeout(() => setCopiado24(false), 2000); }
      else { setCopiado5(true); setTimeout(() => setCopiado5(false), 2000); }
    });
  };

  const renderBanda = (
    banda: '24' | '5',
    nombreRed: string,
    clave: string, setClave: (v: string) => void,
    verClave: boolean, setVerClave: (v: boolean) => void,
    estado: EstadoBanda, errorMsg: string,
    copiado: boolean,
  ) => (
    <div style={{ background: 'var(--sn-superficie)', border: '1px solid var(--sn-borde)', borderRadius: 14, padding: 14, marginBottom: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--sn-marca)', marginBottom: 8 }}>
        📶 Red {banda === '24' ? '2.4GHz' : '5GHz'} {cargandoNombres ? '…' : nombreRed ? `— ${nombreRed}` : ''}
      </div>

      {estado === 'exito' ? (
        <div style={{ background: 'var(--sn-exito-suave)', border: '1px solid var(--sn-exito-suave)', borderRadius: 8, padding: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sn-exito)', marginBottom: 4 }}>✅ Contraseña actualizada</div>
          <div style={{ fontSize: 11, color: 'var(--sn-texto-2)', marginBottom: 2 }}>
            Red: <strong>{nombreRed || `WiFi ${banda === '24' ? '2.4GHz' : '5GHz'}`}</strong>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--sn-texto)', fontFamily: 'monospace' }}>{clave}</span>
            <button
              type="button"
              onClick={() => copiar(clave, banda)}
              style={{
                fontSize: 10, fontWeight: 700, color: 'var(--sn-exito)', background: 'var(--sn-exito-suave)',
                border: '1px solid var(--sn-exito)', borderRadius: 999, padding: '3px 9px', cursor: 'pointer',
              }}
            >
              {copiado ? '✓ Copiado' : '📋 Copiar clave'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <Form.Group>
            <Form.Label style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>Nueva contraseña</Form.Label>
            <div style={{ position: 'relative' }}>
              <Form.Control
                type={verClave ? 'text' : 'password'}
                value={clave}
                onChange={e => setClave(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                disabled={estado === 'enviando'}
              />
              <span
                onClick={() => setVerClave(!verClave)}
                style={{ position: 'absolute', right: 12, top: 9, cursor: 'pointer', fontSize: 15 }}
              >
                {verClave ? '🙈' : '👁️'}
              </span>
            </div>
            {clave.length > 0 && !claveValida(clave) && (
              <div style={{ fontSize: 11, color: 'var(--sn-peligro)', marginTop: 4 }}>Debe tener al menos 8 caracteres.</div>
            )}
          </Form.Group>

          {estado === 'error' && (
            <div style={{ fontSize: 11, color: 'var(--sn-peligro)', marginTop: 6 }}>{errorMsg}</div>
          )}

          <button
            type="button"
            onClick={() => guardarBanda(banda)}
            disabled={!claveValida(clave) || estado === 'enviando'}
            style={{
              width: '100%', marginTop: 8, padding: '9px 12px', border: 'none', borderRadius: 8,
              background: claveValida(clave) ? 'linear-gradient(135deg,var(--sn-accion-hover),var(--sn-accion))' : 'var(--sn-borde-fuerte)',
              color: 'var(--sn-accion-texto)', fontWeight: 700, fontSize: 12,
              cursor: claveValida(clave) ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            {estado === 'enviando' ? <Spinner animation="border" size="sm" /> : `💾 Guardar red ${banda === '24' ? '2.4GHz' : '5GHz'}`}
          </button>
        </>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppNavbar />

      <div style={{ flex: 1, overflowY: 'auto' as const }}>
        <div style={{ background: 'var(--sn-superficie)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--sn-borde)', position: 'sticky', top: 0, zIndex: 10 }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--sn-accion)' }}>
            <FaArrowLeft size={18} />
          </button>
          <div style={{ fontWeight: 700, fontSize: 15 }}>🔐 Cambiar contraseña WiFi</div>
        </div>

        <div style={{ padding: 16, maxWidth: 500, margin: '0 auto' }}>
          {serviciosWifi.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 34 }}>⚠️</div>
              <p style={{ fontSize: 13, color: 'var(--sn-texto-3)', marginTop: 8 }}>No se encontró el router asociado a tu cuenta.</p>
            </div>
          ) : !servicioElegido ? (
            <>
              <p style={{ fontSize: 12, color: 'var(--sn-texto-3)', marginBottom: 14 }}>
                Tienes {serviciosWifi.length} servicios con WiFi. Elige a cuál quieres cambiarle la contraseña:
              </p>
              {serviciosWifi.map((s) => (
                <button
                  key={s.index}
                  type="button"
                  onClick={() => elegirServicio(s)}
                  style={{
                    width: '100%', textAlign: 'left', background: 'var(--sn-superficie)', border: '1px solid var(--sn-borde)', borderRadius: 12,
                    padding: '12px 14px', marginBottom: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                  }}
                >
                  <span style={{
                    flexShrink: 0, width: 26, height: 26, borderRadius: '50%', background: 'var(--sn-marca-suave)',
                    color: 'var(--sn-marca)', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {s.index + 1}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sn-texto)' }}>{s.perfil}</span>
                </button>
              ))}
            </>
          ) : (
            <>
              {serviciosWifi.length > 1 && (
                <button
                  type="button"
                  onClick={() => setServicioElegido(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--sn-marca)', fontSize: 12, fontWeight: 600, padding: 0, marginBottom: 10, cursor: 'pointer' }}
                >
                  ← Elegir otro servicio
                </button>
              )}
              <p style={{ fontSize: 11, color: 'var(--sn-texto-3)', marginBottom: 14 }}>
                {serviciosWifi.length > 1 && <>Servicio: <strong>{servicioElegido.perfil}</strong>. </>}
                Cada red se guarda por separado — puedes cambiar solo una si quieres.
              </p>
              {renderBanda('24', nombre24, clave24, setClave24, verClave24, setVerClave24, estado24, error24, copiado24)}
              {renderBanda('5', nombre5, clave5, setClave5, verClave5, setVerClave5, estado5, error5, copiado5)}
            </>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default CambiarClaveWifi;
